/**
 * The drawing both public screens carry: an exploded axonometry of a corner courtyard block,
 * drawn the way an architect would draw it — line work on paper, no render, no gradient.
 *
 * One object, two callers (retro rule 8): the landing hero's 4:3 plate and the white half of
 * the entrance. It replaces `FloorPlanPattern` on the landing and the rendered glass tower on
 * the entrance (owner's call, 2026-09-12 — the tower read as embarrassing to the architects
 * it was meant to address). `FloorPlanPattern` stays where it is for the B1 project card.
 *
 * **Projection: true isometric.** X recedes at −30°, Y at +30°, Z is vertical and unforeshortened:
 *
 *     sx = 205 + (X − Y)·cos30·S        sy = 215 + (X + Y)·0.5·S − Z·S,   S = 4.05 units/m
 *
 * The module is a 20 × 15 m block with a 3 m chamfer at the street corner, an 8 × 6 m
 * courtyard, 3.15 m storeys on 0.30 m slabs, and a top storey set back 2.5 m from both street
 * faces. The stack is exploded upwards: the plan sits at Z = 0 and the storeys are lifted to
 * 17.5, 23.8, 30.1, 36.4, 42.7 and 49.0 m, which is one clear storey of air between each pair
 * so every wall head is visible. **Hidden lines are removed by painter order alone** — every
 * face is filled `--neutral-0` and drawn far-to-near, with pure occluders in `.mask`. So the
 * order of this file is the order of the sheet, and moving a section moves what it hides.
 *
 * **Every coordinate is computed once, at module scope.** The 470-odd elements come out of
 * the projection helpers and the plan constants rather than being pasted as literals: the
 * arithmetic is deterministic, there is nothing to recompute per render and nothing random,
 * so the same sheet is drawn on every reload. The JSX below maps over the finished arrays.
 *
 * **The annotations are catalog messages**, not literals baked into the geometry. The landing
 * page has an EN/SK/DE switcher in its own footer, and a drawing labelled `DVOR` on an English
 * page is the one element of the screen that did not get translated. Numbers do not go through
 * the catalog — `lib/i18n/formats.ts` has no number helper, so `±0,000` and `3 150` keep the
 * continental drafting spelling (comma decimal, thin-space thousands) in all three languages.
 *
 * **Every colour is a token applied through the stylesheet.** `scripts/guard.mjs` fails the
 * build on a hex anywhere in `src/`. Only two things here are coloured at all: the version
 * cloud (`--danger-500`) and the comment pin (`--pin-comment`) — on a drawing, colour means
 * something, and everything else is the neutral ramp.
 *
 * `aria-hidden`: it is the picture on the wall, not information. A screen reader reading out
 * "courtyard, street, rooflights" would be reading the wallpaper.
 */

import { useId } from 'react';

import { useMessages } from '@/lib/i18n';
import type { MessageKeys } from '@/lib/i18n';

import styles from './AxonometryDrawing.module.css';

/* ------------------------------------------------------------------ projection */

/** A point on the sheet, in viewBox units. */
type Point = readonly [number, number];
/** A point in the building's own plan metres. Z is passed separately, because it is height. */
type Plan = readonly [number, number];

const SCALE = 4.05;
const COS30 = Math.cos(Math.PI / 6);
const KX = COS30 * SCALE;
const KY = 0.5 * SCALE;
const KZ = SCALE;
const OX = 205;
const OY = 215;

/** Metres to sheet units. The one place the projection is written down. */
const at = (x: number, y: number, z = 0): Point => [
  OX + (x - y) * KX,
  OY + (x + y) * KY - z * KZ,
];

const project = (plan: readonly Plan[], z: number): Point[] => plan.map(([x, y]) => at(x, y, z));

const round = (n: number) => Number(n.toFixed(2));
const points = (list: readonly Point[]) =>
  list.map(([x, y]) => `${String(round(x))},${String(round(y))}`).join(' ');
const closedPath = (list: readonly Point[]) =>
  `M${list.map(([x, y]) => `${String(round(x))} ${String(round(y))}`).join('L')}Z`;

/* ------------------------------------------------------------------ line families */

/**
 * The stroke and fill families of the sheet, named rather than numbered. A drawing has a
 * small fixed set of line weights and everything is drawn in one of them; the stylesheet
 * holds the weights and the tokens, this holds the vocabulary.
 */
type Ink =
  | 'heavy'
  | 'medium'
  | 'fine'
  | 'hair'
  | 'faint'
  | 'plan'
  | 'planFine'
  | 'dash'
  | 'dashFaint'
  | 'centre'
  | 'plot'
  | 'version'
  | 'paper'
  | 'slab'
  | 'mask'
  | 'ink'
  | 'pin'
  | 'labelEnd'
  | 'labelStart'
  | 'labelMid'
  | 'dim'
  | 'dimMid'
  | 'versionText'
  | 'pinText';

const CLASSES: Readonly<Record<Ink, string | undefined>> = {
  heavy: styles.heavy,
  medium: styles.medium,
  fine: styles.fine,
  hair: styles.hair,
  faint: styles.faint,
  plan: styles.plan,
  planFine: styles.planFine,
  dash: styles.dash,
  dashFaint: styles.dashFaint,
  centre: styles.centre,
  plot: styles.plot,
  version: styles.version,
  paper: styles.paper,
  slab: styles.slab,
  mask: styles.mask,
  ink: styles.ink,
  pin: styles.pin,
  labelEnd: styles.labelEnd,
  labelStart: styles.labelStart,
  labelMid: styles.labelMid,
  dim: styles.dim,
  dimMid: styles.dimMid,
  versionText: styles.versionText,
  pinText: styles.pinText,
};

const ink = (...names: readonly Ink[]) =>
  names
    .map((name) => CLASSES[name])
    .filter((name): name is string => name !== undefined)
    .join(' ');

/* ------------------------------------------------------------------ the sheet */

/** The eight window and shopfront blocks, drawn once in `<defs>` and placed by `<use>`. */
type OpeningId = 'wa' | 'wb' | 'sa' | 'sb' | 'ca' | 'cb' | 'ya' | 'yb';

type Shape =
  | { readonly kind: 'poly'; readonly cls: string; readonly pts: string; readonly closed: boolean }
  | { readonly kind: 'path'; readonly cls: string; readonly d: string; readonly evenOdd: boolean }
  | { readonly kind: 'line'; readonly cls: string; readonly a: Point; readonly b: Point }
  | {
      readonly kind: 'ellipse';
      readonly cls: string;
      readonly c: Point;
      readonly rx: number;
      readonly ry: number;
    }
  | { readonly kind: 'dot'; readonly cls: string; readonly c: Point; readonly r: number }
  /** A number or a symbol: never translated, see the module header. */
  | { readonly kind: 'glyph'; readonly cls: string; readonly c: Point; readonly text: string }
  | {
      readonly kind: 'label';
      readonly cls: string;
      readonly c: Point;
      readonly message: MessageKeys['drawing'];
    }
  | { readonly kind: 'opening'; readonly symbol: OpeningId; readonly c: Point };

let current: Shape[] = [];

/** Closes the section being drawn and starts the next one. Order on the sheet is order here. */
function section(): readonly Shape[] {
  const done = current;
  current = [];
  return done;
}

function poly(list: readonly Point[], cls: string, closed = true) {
  current.push({ kind: 'poly', cls, pts: points(list), closed });
}

function line(a: Point, b: Point, cls: string) {
  current.push({ kind: 'line', cls, a, b });
}

function shape(d: string, cls: string, evenOdd = false) {
  current.push({ kind: 'path', cls, d, evenOdd });
}

/** A filled band between two outlines — a slab with a courtyard hole, a wall head. */
function ring(outer: readonly Point[], inner: readonly Point[], cls: string) {
  shape(closedPath(outer) + closedPath(inner), cls, true);
}

function ellipse(c: Point, rx: number, ry: number, cls: string) {
  current.push({ kind: 'ellipse', cls, c, rx, ry });
}

function dot(c: Point, r: number, cls: string) {
  current.push({ kind: 'dot', cls, c, r });
}

function glyph(c: Point, text: string, cls: string) {
  current.push({ kind: 'glyph', cls, c, text });
}

function label(c: Point, message: MessageKeys['drawing'], cls: string) {
  current.push({ kind: 'label', cls, c, message });
}

function opening(symbol: OpeningId, c: Point) {
  current.push({ kind: 'opening', symbol, c });
}

/* ------------------------------------------------------------------ the module */

/** Block 20 × 15 m, 3 m chamfer at the street corner (20,15). Outer wall 0.45. */
const OUTER: readonly Plan[] = [
  [0, 0],
  [20, 0],
  [20, 12],
  [17, 15],
  [0, 15],
];
const INNER: readonly Plan[] = [
  [0.45, 0.45],
  [19.55, 0.45],
  [19.55, 11.8136],
  [16.8136, 14.55],
  [0.45, 14.55],
];
/** The courtyard, 8 × 6 m, and its wall face 0.30 outside it. */
const COURT: readonly Plan[] = [
  [6, 4],
  [14, 4],
  [14, 10],
  [6, 10],
];
const COURT_WALL: readonly Plan[] = [
  [5.7, 3.7],
  [14.3, 3.7],
  [14.3, 10.3],
  [5.7, 10.3],
];
/** The top storey and the roof: 2.5 m back from both street faces. */
const SET: readonly Plan[] = [
  [0, 0],
  [17.5, 0],
  [17.5, 9.5],
  [14.5, 12.5],
  [0, 12.5],
];
const SET_INNER: readonly Plan[] = [
  [0.45, 0.45],
  [17.05, 0.45],
  [17.05, 9.3136],
  [14.3136, 12.05],
  [0.45, 12.05],
];

const STOREY = 3.15;
const SLAB = 0.3;
/** 1.NP … 5.NP and the roof. The first lift is the plan's own depth, so the plan clears the stack. */
const LEVELS = [17.5, 23.8, 30.1, 36.4, 42.7, 49] as const;

/** The chamfer, and the line the ground-floor arcade is recessed back to. */
const CHAMFER_A: Plan = [20, 12];
const CHAMFER_B: Plan = [17, 15];
const RECESS = 2.2 / Math.SQRT2;
const ARCADE_A: Plan = [CHAMFER_A[0] - RECESS, CHAMFER_A[1] - RECESS];
const ARCADE_B: Plan = [CHAMFER_B[0] - RECESS, CHAMFER_B[1] - RECESS];
/** Where the two arcade columns stand, as a fraction along the chamfer. */
const COLUMNS = [0.27, 0.73] as const;

/* ------------------------------------------------------------------ openings */

/**
 * One window, shopfront or rooflight, in the plane of the wall it sits in and relative to its
 * own lower outer corner. `sign` is +1 for an opening running along +X on a Y-plane wall and
 * −1 for one running along +Y on an X-plane wall — the two directions a wall can face here.
 */
function block(sign: 1 | -1, wide: number, high: number, inset: number, mullions: number) {
  const local = (u: number, v: number): Point => [u * sign * KX, u * KY - v * KZ];
  const shapes: Shape[] = [];
  shapes.push({
    kind: 'poly',
    cls: ink('paper', 'fine'),
    pts: points([local(0, 0), local(wide, 0), local(wide, high), local(0, high)]),
    closed: true,
  });
  if (inset > 0) {
    shapes.push({
      kind: 'poly',
      cls: ink('hair'),
      pts: points([
        local(inset, inset),
        local(wide - inset, inset),
        local(wide - inset, high - inset),
        local(inset, high - inset),
      ]),
      closed: true,
    });
  }
  for (let m = 1; m <= mullions; m += 1) {
    const u = (wide * m) / (mullions + 1);
    shapes.push({ kind: 'line', cls: ink('hair'), a: local(u, inset), b: local(u, high - inset) });
  }
  // The sill runs a little past the reveal on both sides, which is what makes it read as a sill.
  shapes.push({
    kind: 'line',
    cls: ink('fine'),
    a: local(-0.18, 0),
    b: local(wide + 0.18, 0),
  });
  return shapes;
}

const OPENINGS: readonly { readonly id: OpeningId; readonly shapes: readonly Shape[] }[] = [
  { id: 'wa', shapes: block(1, 1.4, 1.7, 0.3, 1) }, // apartment window, Y-plane
  { id: 'wb', shapes: block(-1, 1.4, 1.7, 0.3, 1) }, // apartment window, X-plane
  { id: 'sa', shapes: block(1, 3.4, 2.55, 0.28, 2) }, // shopfront, Y-plane
  { id: 'sb', shapes: block(-1, 3.4, 2.55, 0.28, 2) }, // shopfront, X-plane
  { id: 'ca', shapes: block(1, 2.6, 2.3, 0.28, 1) }, // penthouse glazing, Y-plane
  { id: 'cb', shapes: block(-1, 2.6, 2.3, 0.28, 1) }, // penthouse glazing, X-plane
  { id: 'ya', shapes: block(1, 1.2, 1.5, 0.26, 0) }, // courtyard window, Y-plane
  { id: 'yb', shapes: block(-1, 1.2, 1.5, 0.26, 0) }, // courtyard window, X-plane
];

/* ------------------------------------------------------------------ context */

/**
 * The street: three concentric curves around the corner — the far kerb, the carriageway
 * centre line and the near kerb — each running off the sheet at both ends.
 */
function kerb(radius: number, cx: number, cy: number, cls: string) {
  const list: Plan[] = [
    [cx + radius, -34],
    [cx + radius, cy],
  ];
  for (let k = 1; k <= 8; k += 1) {
    const t = (Math.PI / 2) * (k / 8);
    list.push([cx + radius * Math.cos(t), cy + radius * Math.sin(t)]);
  }
  list.push([-42, cy + radius]);
  poly(project(list, 0), cls, false);
}

/** A neighbour: an outline box, filled white, no windows. It is scenery, not a building. */
function neighbour(x0: number, y0: number, x1: number, y1: number) {
  poly([at(x0, y0), at(x1, y0), at(x1, y1), at(x0, y1)], ink('paper', 'faint'));
}

kerb(11, 19, 14, ink('faint'));
kerb(7.5, 19, 14, ink('centre'));
kerb(4, 19, 14, ink('planFine'));
neighbour(-8, 0, 0, 15); // party wall along X = 0
neighbour(0, -7, 20, 0); // party wall along Y = 0
const CONTEXT = section();

neighbour(-2, 25, 8, 33);
neighbour(-15, 20, -5, 29);
neighbour(28, -4, 38, 6);
const CONTEXT_FRONT = section();

/* ------------------------------------------------------------------ ground plan */

/** A cross wall, drawn as the pair of lines a plan draws it as. */
function wallAlongY(x: number, y0: number, y1: number, t = 0.2) {
  poly(
    [at(x - t / 2, y0), at(x + t / 2, y0), at(x + t / 2, y1), at(x - t / 2, y1)],
    ink('planFine'),
  );
}
function wallAlongX(y: number, x0: number, x1: number, t = 0.2) {
  poly(
    [at(x0, y - t / 2), at(x1, y - t / 2), at(x1, y + t / 2), at(x0, y + t / 2)],
    ink('planFine'),
  );
}

/** A stair core: the flight, its treads and the run arrow. */
function stair(x0: number, y0: number, x1: number, y1: number, treads: number) {
  const cls = ink('planFine');
  poly([at(x0, y0), at(x1, y0), at(x1, y1), at(x0, y1)], cls);
  for (let k = 1; k < treads; k += 1) {
    const y = y0 + ((y1 - y0) * k) / treads;
    line(at(x0, y), at(x1, y), cls);
  }
  line(at((x0 + x1) / 2, y0 + 0.4), at((x0 + x1) / 2, y1 - 0.4), cls);
}

/** White out the wall band where an opening goes, before the jambs are drawn over it. */
function erase(corners: readonly Plan[]) {
  poly(
    corners.map(([x, y]) => at(x, y)),
    ink('mask'),
  );
}

/** A door leaf and its swing, sampled as a projected elliptical arc. */
function swing(hinge: Plan, radius: number, from: number, to: number) {
  const cls = ink('planFine');
  const list: Point[] = [];
  for (let k = 0; k <= 12; k += 1) {
    const t = ((from + ((to - from) * k) / 12) * Math.PI) / 180;
    list.push(at(hinge[0] + radius * Math.cos(t), hinge[1] + radius * Math.sin(t)));
  }
  poly(list, cls, false);
  const last = list[list.length - 1];
  if (last !== undefined) line(at(hinge[0], hinge[1]), last, cls);
}

/** The drafting symbol for a tree: a circle and a centre cross, nothing botanical. */
function tree(cx: number, cy: number, r = 1.3) {
  const cls = ink('planFine');
  ellipse(at(cx, cy), r * KX * Math.SQRT2, r * KY * Math.SQRT2, cls);
  line(at(cx - r * 0.62, cy), at(cx + r * 0.62, cy), cls);
  line(at(cx, cy - r * 0.62), at(cx, cy + r * 0.62), cls);
}

// The footprint is filled white first, so the kerb curves do not run under the building.
shape(closedPath(project(OUTER, 0)), ink('paper', 'plan'));
poly(project(INNER, 0), ink('plan'));
poly(project(COURT, 0), ink('plan'));
poly(project(COURT_WALL, 0), ink('plan'));

wallAlongY(4, 10.3, 14.55);
wallAlongY(15.6, 10.3, 14.55);
wallAlongY(6.5, 0.45, 3.7);
wallAlongY(13.5, 0.45, 3.7);
wallAlongX(5.2, 0.45, 5.7);
wallAlongX(10.2, 0.45, 5.7);
wallAlongX(5.2, 14.3, 19.55);
wallAlongX(9.8, 14.3, 19.55);

stair(1.3, 5.8, 4.6, 9.6, 7);
stair(15.9, 5.8, 19.1, 9.6, 7);

// The passage through the front wing, street to courtyard, 3 m wide.
erase([
  [8.6, 14.4],
  [11.6, 14.4],
  [11.6, 15.1],
  [8.6, 15.1],
]);
erase([
  [8.6, 9.9],
  [11.6, 9.9],
  [11.6, 10.4],
  [8.6, 10.4],
]);
for (const x of [8.6, 11.6]) line(at(x, 10), at(x, 15), ink('plan'));
line(at(8.6, 14.55), at(11.6, 14.55), ink('planFine'));

// Shop doors.
erase([
  [3, 14.4],
  [4.3, 14.4],
  [4.3, 15.1],
  [3, 15.1],
]);
erase([
  [19.45, 5],
  [20.1, 5],
  [20.1, 6.3],
  [19.45, 6.3],
]);
swing([3, 14.9], 1.3, 0, 88);
swing([11.6, 14.9], 1.45, 180, 268);
swing([19.6, 5], 1.3, 90, 178);

// The corner arcade: the ground floor steps 2.2 m back behind the chamfer.
erase([
  [20.25, 12.25],
  [16.75, 15.75],
  [16.3, 14],
  [19.3, 11],
]);
line(at(...CHAMFER_A), at(...ARCADE_A), ink('plan'));
line(at(...CHAMFER_B), at(...ARCADE_B), ink('plan'));
line(at(...ARCADE_A), at(...ARCADE_B), ink('plan'));
line(
  at(ARCADE_A[0] - 0.318, ARCADE_A[1] - 0.318),
  at(ARCADE_B[0] - 0.318, ARCADE_B[1] - 0.318),
  ink('planFine'),
);
for (const t of COLUMNS) {
  const cx = CHAMFER_A[0] + (CHAMFER_B[0] - CHAMFER_A[0]) * t;
  const cy = CHAMFER_A[1] + (CHAMFER_B[1] - CHAMFER_A[1]) * t;
  poly(
    [at(cx - 0.3, cy - 0.3), at(cx + 0.3, cy - 0.3), at(cx + 0.3, cy + 0.3), at(cx - 0.3, cy + 0.3)],
    ink('paper', 'plan'),
  );
}
const ARCADE_MID: Plan = [
  (ARCADE_A[0] + ARCADE_B[0]) / 2,
  (ARCADE_A[1] + ARCADE_B[1]) / 2,
];
swing([ARCADE_MID[0] + 0.6, ARCADE_MID[1] - 0.6], 1.5, 45, 133);

// The plot boundary, 0.9 m outside the building line.
poly(
  project(
    [
      [-0.9, -0.9],
      [20.9, -0.9],
      [20.9, 12.3728],
      [17.3728, 15.9],
      [-0.9, 15.9],
    ],
    0,
  ),
  ink('plot'),
);

tree(21.5, 2.6);
tree(21.5, 9.4);
tree(9.5, 16.5);

// The north point, lying in the plane of the plan like everything else on it.
const NORTH: Plan = [12, 22.6];
ellipse(
  at(...NORTH),
  1.75 * KX * Math.SQRT2,
  1.75 * KY * Math.SQRT2,
  ink('paper', 'planFine'),
);
poly(
  [
    at(NORTH[0], NORTH[1] - 2.3),
    at(NORTH[0] - 0.8, NORTH[1] + 1.7),
    at(NORTH[0], NORTH[1] + 0.75),
    at(NORTH[0] + 0.8, NORTH[1] + 1.7),
  ],
  ink('paper', 'plan'),
);
const NORTH_LETTER = at(NORTH[0], NORTH[1] - 3);
label([NORTH_LETTER[0], NORTH_LETTER[1] - 0.6], 'north', ink('labelMid'));

const COURT_LABEL = at(10, 7);
label([COURT_LABEL[0], COURT_LABEL[1] + 1.4], 'courtyard', ink('labelMid'));
label(at(27.4, 6.6), 'street', ink('labelMid'));
const GROUND_PLAN = section();

/* ------------------------------------------------------------------ projection lines */

// The lines that say this is one building taken apart, rather than six buildings.
for (const [x, y] of OUTER.slice(1)) line(at(x, y, 0), at(x, y, LEVELS[4]), ink('dash'));
for (const [x, y] of SET.slice(1)) line(at(x, y, LEVELS[4]), at(x, y, LEVELS[5]), ink('dashFaint'));
const PROJECTION = section();

/* ------------------------------------------------------------------ the storeys */

/** A vertical face between two plan points, from z0 up to z1. */
function face(a: Plan, b: Plan, z0: number, z1: number, cls: string) {
  poly([at(a[0], a[1], z0), at(b[0], b[1], z0), at(b[0], b[1], z1), at(a[0], a[1], z1)], cls);
}

/**
 * A floor slab: the top face with the courtyard punched out of it, then the thickness band on
 * the edges whose outward normal faces the viewer. Which edges those are follows from the
 * plan's clockwise ordering, so it is computed rather than listed.
 */
function slab(outer: readonly Plan[], hole: readonly Plan[], z: number) {
  ring(project(outer, z), project(hole, z), ink('slab', 'medium'));
  for (let i = 0; i < outer.length; i += 1) {
    const a = outer[i];
    const b = outer[(i + 1) % outer.length];
    if (a === undefined || b === undefined) continue;
    const normal = b[1] - a[1] + (a[0] - b[0]);
    if (normal > 1e-6) face(a, b, z - SLAB, z, ink('paper', 'medium'));
  }
}

/** The two courtyard faces whose normal points at the viewer — the only ones that are seen. */
function courtyardWalls(z: number, windows: boolean) {
  const [x0, y0] = COURT[0] ?? [0, 0];
  const [x1, y1] = COURT[2] ?? [0, 0];
  face([x0, y0], [x0, y1], z, z + STOREY, ink('paper', 'fine'));
  face([x0, y0], [x1, y0], z, z + STOREY, ink('paper', 'fine'));
  if (!windows) return;
  for (const y of [y0 + 1.2, y0 + 3.4]) opening('yb', at(x0, y + 1.2, z + 0.95));
  for (const x of [x0 + 1.6, x0 + 4.4]) opening('ya', at(x, y0, z + 0.95));
}

/** The near wings of the courtyard are behind the block's own street walls: occlude them. */
function nearWingMasks(z: number) {
  const [x0, y0] = COURT[0] ?? [0, 0];
  const [x1, y1] = COURT[2] ?? [0, 0];
  const cls = ink('mask');
  poly(
    [at(x1, y0, z), at(x1, y1, z), at(x1, y1, z + STOREY), at(x1, y0, z + STOREY)],
    cls,
  );
  poly(
    [at(x0, y1, z), at(x1, y1, z), at(x1, y1, z + STOREY), at(x0, y1, z + STOREY)],
    cls,
  );
}

/** The wall heads: what you see of a storey from above once the slab over it is gone. */
function wallHeads(z: number, outer: readonly Plan[], inner: readonly Plan[]) {
  ring(project(outer, z + STOREY), project(inner, z + STOREY), ink('paper', 'medium'));
  ring(project(COURT_WALL, z + STOREY), project(COURT, z + STOREY), ink('paper', 'fine'));
}

/** 1.NP — the shops and the entrance, with the arcade cut into the corner. */
function groundStorey(z: number) {
  slab(OUTER, COURT, z);
  courtyardWalls(z, true);
  nearWingMasks(z);
  wallHeads(z, OUTER, INNER);

  face([0, 15], [17, 15], z, z + STOREY, ink('paper', 'heavy'));
  for (const x of [1.2, 5.4, 9.6, 13.2]) opening('sa', at(x, 15, z + 0.35));
  face([20, 0], [20, 12], z, z + STOREY, ink('paper', 'heavy'));
  for (const y of [1, 5.2]) opening('sb', at(20, y + 3.4, z + 0.35));

  const fine = ink('fine');
  poly(
    [
      at(ARCADE_A[0], ARCADE_A[1], z),
      at(ARCADE_B[0], ARCADE_B[1], z),
      at(ARCADE_B[0], ARCADE_B[1], z + STOREY),
      at(ARCADE_A[0], ARCADE_A[1], z + STOREY),
    ],
    ink('paper', 'fine'),
  );
  line(at(CHAMFER_A[0], CHAMFER_A[1], z + STOREY), at(ARCADE_A[0], ARCADE_A[1], z + STOREY), fine);
  line(at(CHAMFER_B[0], CHAMFER_B[1], z + STOREY), at(ARCADE_B[0], ARCADE_B[1], z + STOREY), fine);
  for (const t of COLUMNS) {
    const cx = CHAMFER_A[0] + (CHAMFER_B[0] - CHAMFER_A[0]) * t;
    const cy = CHAMFER_A[1] + (CHAMFER_B[1] - CHAMFER_A[1]) * t;
    poly(
      [
        at(cx - 0.3, cy + 0.3, z),
        at(cx + 0.3, cy + 0.3, z),
        at(cx + 0.3, cy + 0.3, z + STOREY),
        at(cx - 0.3, cy + 0.3, z + STOREY),
      ],
      ink('paper', 'fine'),
    );
    line(at(cx + 0.3, cy + 0.3, z), at(cx + 0.3, cy + 0.3, z + STOREY), fine);
  }
  // The soffit over the arcade, which is what makes the recess read as a depth.
  poly(
    [
      at(CHAMFER_A[0], CHAMFER_A[1], z + STOREY),
      at(CHAMFER_B[0], CHAMFER_B[1], z + STOREY),
      at(ARCADE_B[0], ARCADE_B[1], z + STOREY),
      at(ARCADE_A[0], ARCADE_A[1], z + STOREY),
    ],
    ink('paper', 'fine'),
  );
}

/** The balcony on the street facade: from x, to x, and how far it projects. */
const BALCONY = { from: 7, to: 10.6, depth: 1.4 } as const;

/** 2.–4.NP — the apartments. The middle one takes a loggia instead of a fourth window. */
function flatStorey(z: number, index: number) {
  slab(OUTER, COURT, z);
  courtyardWalls(z, true);
  nearWingMasks(z);
  wallHeads(z, OUTER, INNER);

  const fine = ink('fine');
  const faint = ink('faint');

  face([0, 15], [17, 15], z, z + STOREY, ink('paper', 'heavy'));
  for (const x of [1.3, 3.9, 12, 14.6]) opening('wa', at(x, 15, z + 0.9));
  opening('wa', at(8.4, 15, z + 0.35)); // the balcony door, down at floor level

  const { from, to, depth } = BALCONY;
  const edge = 15 + depth;
  poly([at(from, 15, z), at(to, 15, z), at(to, edge, z), at(from, edge, z)], ink('paper', 'fine'));
  face([from, edge], [to, edge], z - 0.22, z, ink('paper', 'fine'));
  face([to, edge], [to, 15], z - 0.22, z, ink('paper', 'fine'));
  const rail = 1.05;
  const railings: readonly (readonly [Plan, Plan])[] = [
    [
      [from, edge],
      [to, edge],
    ],
    [
      [to, edge],
      [to, 15],
    ],
  ];
  for (const [a, b] of railings) {
    line(at(a[0], a[1], z + rail), at(b[0], b[1], z + rail), fine);
    line(at(a[0], a[1], z + rail * 0.45), at(b[0], b[1], z + rail * 0.45), faint);
  }
  for (let k = 0; k <= 7; k += 1) {
    const x = from + ((to - from) * k) / 7;
    line(at(x, edge, z), at(x, edge, z + rail), faint);
  }
  for (let k = 0; k < 4; k += 1) {
    const y = 15 + (depth * k) / 3;
    line(at(to, y, z), at(to, y, z + rail), faint);
  }
  line(at(from, edge, z), at(from, edge, z + rail), fine);

  if (index === 2) {
    // The loggia: a 2.5 m high hole cut out of the facade rather than a box hung on it.
    const y0 = 7.8;
    const y1 = 10.8;
    const back = 20 - 1.6;
    poly([at(back, y0, z), at(20, y0, z), at(20, y1, z), at(back, y1, z)], ink('paper', 'fine'));
    face([back, y0], [back, y1], z, z + 2.5, ink('paper', 'fine'));
    face([back, y0], [20, y0], z, z + 2.5, ink('paper', 'fine'));
    shape(
      closedPath([at(20, 0, z), at(20, 12, z), at(20, 12, z + STOREY), at(20, 0, z + STOREY)]) +
        closedPath([at(20, y0, z), at(20, y1, z), at(20, y1, z + 2.5), at(20, y0, z + 2.5)]),
      ink('paper', 'heavy'),
      true,
    );
    line(at(20, y0, z + 2.5), at(20, y1, z + 2.5), fine);
    for (let k = 0; k <= 4; k += 1) {
      const y = y0 + ((y1 - y0) * k) / 4;
      line(at(20, y, z), at(20, y, z + 1), faint);
    }
    line(at(20, y0, z + 1), at(20, y1, z + 1), fine);
    for (const y of [1, 3.9]) opening('wb', at(20, y + 1.4, z + 0.9));
  } else {
    face([20, 0], [20, 12], z, z + STOREY, ink('paper', 'heavy'));
    for (const y of [1, 3.9, 6.8, 9.7]) opening('wb', at(20, y + 1.4, z + 0.9));
  }

  face(CHAMFER_A, CHAMFER_B, z, z + STOREY, ink('paper', 'heavy'));
  const mx = (CHAMFER_A[0] + CHAMFER_B[0]) / 2;
  const my = (CHAMFER_A[1] + CHAMFER_B[1]) / 2;
  poly(
    [
      at(mx + 0.5, my + 0.5, z + 0.9),
      at(mx - 0.5, my - 0.5, z + 0.9),
      at(mx - 0.5, my - 0.5, z + 2.6),
      at(mx + 0.5, my + 0.5, z + 2.6),
    ],
    ink('paper', 'fine'),
  );
}

/** 5.NP — set back from both street faces, so the storey below it gains a terrace. */
function setStorey(z: number) {
  slab(OUTER, COURT, z);
  courtyardWalls(z, true);
  nearWingMasks(z);
  ring(project(SET, z + STOREY), project(SET_INNER, z + STOREY), ink('paper', 'medium'));
  ring(project(COURT_WALL, z + STOREY), project(COURT, z + STOREY), ink('paper', 'fine'));

  face([0, 12.5], [14.5, 12.5], z, z + STOREY, ink('paper', 'heavy'));
  face([17.5, 0], [17.5, 9.5], z, z + STOREY, ink('paper', 'heavy'));
  face([17.5, 9.5], [14.5, 12.5], z, z + STOREY, ink('paper', 'heavy'));
  for (const x of [1.2, 5, 10.6]) opening('ca', at(x, 12.5, z + 0.35));
  for (const y of [1.2, 5]) opening('cb', at(17.5, y + 2.6, z + 0.35));

  const fine = ink('fine');
  const faint = ink('faint');
  const rail = 1.05;
  const edges: readonly (readonly [Plan, Plan])[] = [
    [
      [0, 15],
      [17, 15],
    ],
    [
      [17, 15],
      [20, 12],
    ],
    [
      [20, 12],
      [20, 0],
    ],
  ];
  for (const [a, b] of edges) {
    line(at(a[0], a[1], z + rail), at(b[0], b[1], z + rail), fine);
    line(at(a[0], a[1], z + rail * 0.42), at(b[0], b[1], z + rail * 0.42), faint);
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const n = Math.max(2, Math.round(length / 1.7));
    for (let k = 0; k <= n; k += 1) {
      const t = k / n;
      line(
        at(a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, z),
        at(a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, z + rail),
        faint,
      );
    }
  }
  // Paving joints on the terrace, which is what tells it apart from a roof.
  for (let k = 1; k <= 5; k += 1) {
    const x = 1 + k * 2.8;
    if (x < 16) line(at(x, 12.5, z), at(x, 15, z), faint);
  }
  for (let k = 1; k <= 4; k += 1) {
    const y = 0.8 + k * 2.6;
    if (y < 11) line(at(17.5, y, z), at(20, y, z), faint);
  }
}

/** The roof: parapet, rooflights and the falls. */
function roof(z: number) {
  slab(SET, COURT, z);
  const faint = ink('faint');
  const paperFine = ink('paper', 'fine');

  face([6, 4], [6, 10], z, z + 0.7, paperFine);
  face([6, 4], [14, 4], z, z + 0.7, paperFine);
  ring(project(COURT_WALL, z + 0.7), project(COURT, z + 0.7), paperFine);

  for (const [x, y] of [
    [2.2, 1.6],
    [2.2, 7.4],
    [15.4, 1.6],
    [15.4, 6.4],
  ] as readonly Plan[]) {
    poly(
      [
        at(x, y, z + 0.5),
        at(x + 1.4, y, z + 0.5),
        at(x + 1.4, y + 1.4, z + 0.5),
        at(x, y + 1.4, z + 0.5),
      ],
      paperFine,
    );
    face([x + 1.4, y], [x + 1.4, y + 1.4], z, z + 0.5, paperFine);
    face([x, y + 1.4], [x + 1.4, y + 1.4], z, z + 0.5, paperFine);
    line(at(x, y, z + 0.5), at(x + 1.4, y + 1.4, z + 0.5), faint);
  }

  for (const [x, y] of [
    [9, 1.2],
    [9, 11.2],
  ] as readonly Plan[]) {
    line(at(x, y, z), at(x + 2.6, y, z), faint);
    line(at(x + 2.6, y, z), at(x + 2, y - 0.4, z), faint);
    line(at(x + 2.6, y, z), at(x + 2, y + 0.4, z), faint);
  }

  const parapet = 0.9;
  const medium = ink('paper', 'medium');
  face([0, 12.5], [14.5, 12.5], z, z + parapet, medium);
  face([14.5, 12.5], [17.5, 9.5], z, z + parapet, medium);
  face([17.5, 9.5], [17.5, 0], z, z + parapet, medium);
  ring(
    project(SET, z + parapet),
    project(
      [
        [0.25, 0.25],
        [17.25, 0.25],
        [17.25, 9.6],
        [14.6, 12.25],
        [0.25, 12.25],
      ],
      z + parapet,
    ),
    medium,
  );
}

groundStorey(LEVELS[0]);
flatStorey(LEVELS[1], 1);
flatStorey(LEVELS[2], 2);
flatStorey(LEVELS[3], 3);
setStorey(LEVELS[4]);
roof(LEVELS[5]);
const STOREYS = section();

/* ------------------------------------------------------------------ marks */

/**
 * A version cloud: the rectangle walked clockwise with an outward arc per step. This is the
 * one mark on the sheet that is not architecture — it is the product, drawn on the drawing.
 */
function cloud(x0: number, y0: number, x1: number, y1: number, step = 4.2, r = 2.7) {
  const corners: readonly Point[] = [
    [x0, y0],
    [x1, y0],
    [x1, y1],
    [x0, y1],
  ];
  const d: string[] = [];
  for (let i = 0; i < 4; i += 1) {
    const a = corners[i];
    const b = corners[(i + 1) % 4];
    if (a === undefined || b === undefined) continue;
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const k = Math.max(2, Math.round(length / step));
    if (i === 0) d.push(`M${points([a])}`);
    for (let j = 1; j <= k; j += 1) {
      const t = j / k;
      const q: Point = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
      const rr = Math.max(r, (length / k) * 0.55);
      d.push(`A${String(round(rr))},${String(round(rr))} 0 0 1 ${points([q])}`);
    }
  }
  d.push('Z');
  shape(d.join(''), ink('version'));
}

const CLOUDED = (() => {
  const z = LEVELS[2];
  const { from, to, depth } = BALCONY;
  const box: Point[] = [];
  for (const dz of [-0.4, 1.3]) {
    box.push(
      at(from, 15, z + dz),
      at(to, 15, z + dz),
      at(to, 15 + depth, z + dz),
      at(from, 15 + depth, z + dz),
    );
  }
  return {
    x0: Math.min(...box.map(([x]) => x)) - 2.8,
    y0: Math.min(...box.map(([, y]) => y)) - 2.8,
    x1: Math.max(...box.map(([x]) => x)) + 2.8,
    y1: Math.max(...box.map(([, y]) => y)) + 2.8,
  };
})();

cloud(CLOUDED.x0, CLOUDED.y0, CLOUDED.x1, CLOUDED.y1);
const MARK_X = CLOUDED.x0 - 8.5;
const MARK_Y = CLOUDED.y0 - 1;
poly(
  [
    [MARK_X, MARK_Y - 3.4],
    [MARK_X - 3, MARK_Y + 2],
    [MARK_X + 3, MARK_Y + 2],
  ],
  ink('version'),
);
glyph([MARK_X, MARK_Y + 1.2], '2', ink('versionText'));
line([CLOUDED.x0 + 1.6, CLOUDED.y0 + 3.4], [MARK_X + 2.2, MARK_Y + 1.6], ink('version'));

const PIN = at(13.4, 15, LEVELS[1] + 2.4);
dot(PIN, 3.2, ink('pin'));
glyph([PIN[0], PIN[1] + 1.25], '5', ink('pinText'));
const MARKS = section();

/* ------------------------------------------------------------------ annotations */

/** Where the storey labels line up, and where the bracket beside them stands. */
const LABEL_X = 140;
const BRACKET_X = 146;
/** The datum staff's spine, in the right-hand margin. */
const STAFF_X = 334;

/**
 * A label with a leader: out sideways from the text, then across to a dot on the thing named.
 * The dog-leg is what keeps the label clear of the drawing at any length of translation.
 */
function leader(y: number, target: Point, message: MessageKeys['drawing']) {
  const midpoint = (LABEL_X + target[0]) / 2;
  line([LABEL_X + 3, y - 1.3], [midpoint, y - 1.3], ink('hair'));
  line([midpoint, y - 1.3], target, ink('hair'));
  dot(target, 0.85, ink('ink'));
  label([LABEL_X, y], message, ink('labelEnd'));
}

const ROOF_POINT = at(0, 12.5, LEVELS[5]);
const SET_POINT = at(0, 15, LEVELS[4]);
const FLAT_POINT = at(0, 15, LEVELS[2]);
const SHOP_POINT = at(0, 15, LEVELS[0]);
leader(ROOF_POINT[1], ROOF_POINT, 'level.roof');
leader(SET_POINT[1], SET_POINT, 'level.penthouse');
leader(FLAT_POINT[1], FLAT_POINT, 'level.flats');
leader(SHOP_POINT[1], SHOP_POINT, 'level.ground');

// The bracket that says the three apartment storeys are one label, not three.
line(
  [BRACKET_X, at(0, 15, LEVELS[1])[1]],
  [BRACKET_X, at(0, 15, LEVELS[3])[1]],
  ink('hair'),
);
for (const z of [LEVELS[1], LEVELS[2], LEVELS[3]]) {
  const y = at(0, 15, z)[1];
  line([BRACKET_X, y], [BRACKET_X + 3.2, y], ink('hair'));
}

// The storey height, dimensioned on the right vertical edge of 3.NP.
const DIM_OFFSET = 12;
const DIM_FOOT = at(20, 0, LEVELS[2]);
const DIM_HEAD = at(20, 0, LEVELS[2] + STOREY);
line(DIM_FOOT, [DIM_FOOT[0] + DIM_OFFSET + 3.5, DIM_FOOT[1]], ink('faint'));
line(DIM_HEAD, [DIM_HEAD[0] + DIM_OFFSET + 3.5, DIM_HEAD[1]], ink('faint'));
line(
  [DIM_FOOT[0] + DIM_OFFSET, DIM_FOOT[1]],
  [DIM_HEAD[0] + DIM_OFFSET, DIM_HEAD[1]],
  ink('hair'),
);
for (const end of [DIM_FOOT, DIM_HEAD]) {
  // The 45° tick a dimension line ends in, rather than an arrowhead.
  line(
    [end[0] + DIM_OFFSET - 1.7, end[1] + 1.7],
    [end[0] + DIM_OFFSET + 1.7, end[1] - 1.7],
    ink('hair'),
  );
}
label(
  [DIM_FOOT[0] + DIM_OFFSET + 2.4, (DIM_FOOT[1] + DIM_HEAD[1]) / 2 + 1.3],
  'dimension.storey',
  ink('dim'),
);

/**
 * The level datum staff. Six heights above finished floor, in the continental drafting
 * spelling — a comma decimal and three places, which is what these read as on a real sheet.
 * They are numbers and do not go through the catalog (see the module header).
 */
const DATUM = ['±0,000', '+3,150', '+6,300', '+9,450', '+12,600', '+15,750'] as const;
const DATUM_Y: number[] = [];
for (let i = 0; i < DATUM.length; i += 1) {
  const level = LEVELS[i] ?? 0;
  const [cx, cy] = i === 5 ? [17.5, 0] : [20, 0];
  const q = at(cx, cy, level);
  // 3.NP's tie-line starts further out, so it clears the storey dimension sitting there.
  const x0 = i === 2 ? 300 : q[0] + 3.5;
  line([x0, q[1]], [STAFF_X, q[1]], ink('faint'));
  poly(
    [
      [STAFF_X, q[1]],
      [STAFF_X - 2.5, q[1] - 2.7],
      [STAFF_X + 2.5, q[1] - 2.7],
    ],
    ink('ink'),
  );
  glyph([STAFF_X + 4.5, q[1] + 1.3], DATUM[i] ?? '', ink('dim'));
  DATUM_Y.push(q[1]);
}
line(
  [STAFF_X, DATUM_Y[0] ?? 0],
  [STAFF_X, DATUM_Y[DATUM_Y.length - 1] ?? 0],
  ink('hair'),
);

const ROOFLIGHT = at(16.8, 1.6, LEVELS[5] + 0.5);
line(ROOFLIGHT, [ROOFLIGHT[0] + 14, ROOFLIGHT[1] - 11], ink('hair'));
line([ROOFLIGHT[0] + 14, ROOFLIGHT[1] - 11], [ROOFLIGHT[0] + 30, ROOFLIGHT[1] - 11], ink('hair'));
dot(ROOFLIGHT, 0.85, ink('ink'));
label([ROOFLIGHT[0] + 32, ROOFLIGHT[1] - 9.6], 'rooflights', ink('labelStart'));

// The scale bar, bottom left, alternating filled and open 5 m divisions.
const BAR_X = 26;
const BAR_Y = 284;
line([BAR_X, BAR_Y], [BAR_X + 48, BAR_Y], ink('plan'));
for (let k = 0; k < 5; k += 1) {
  line([BAR_X + k * 12, BAR_Y], [BAR_X + k * 12, BAR_Y - 2.8], ink('plan'));
}
for (const start of [0, 24]) {
  poly(
    [
      [BAR_X + start, BAR_Y],
      [BAR_X + start + 12, BAR_Y],
      [BAR_X + start + 12, BAR_Y - 2.8],
      [BAR_X + start, BAR_Y - 2.8],
    ],
    ink('ink'),
  );
}
glyph([BAR_X, BAR_Y + 6.2], '0', ink('dimMid'));
glyph([BAR_X + 48, BAR_Y + 6.2], '20 m', ink('dimMid'));
label([BAR_X, BAR_Y - 6.4], 'scale', ink('dim'));
const ANNOTATIONS = section();

/* ------------------------------------------------------------------ the component */

const SECTIONS: readonly (readonly Shape[])[] = [
  CONTEXT,
  CONTEXT_FRONT,
  GROUND_PLAN,
  PROJECTION,
  STOREYS,
  MARKS,
  ANNOTATIONS,
];

export interface AxonometryDrawingProps {
  /**
   * How the sheet meets a frame that is not 4:3. `cover` fills it and crops the margins;
   * `contain` fits the whole sheet inside it and leaves paper around it. The entrance's
   * plate is square, where `cover` would take the scale bar and the datum staff off both
   * edges — so that caller asks for `contain`.
   */
  readonly fit?: 'cover' | 'contain';
}

function draw(shapeToDraw: Shape, key: number, scope: string, t: (m: MessageKeys['drawing']) => string) {
  switch (shapeToDraw.kind) {
    case 'poly':
      return shapeToDraw.closed ? (
        <polygon key={key} className={shapeToDraw.cls} points={shapeToDraw.pts} />
      ) : (
        <polyline key={key} className={shapeToDraw.cls} points={shapeToDraw.pts} />
      );
    case 'path':
      return (
        <path
          key={key}
          className={shapeToDraw.cls}
          d={shapeToDraw.d}
          fillRule={shapeToDraw.evenOdd ? 'evenodd' : undefined}
        />
      );
    case 'line':
      return (
        <line
          key={key}
          className={shapeToDraw.cls}
          x1={round(shapeToDraw.a[0])}
          y1={round(shapeToDraw.a[1])}
          x2={round(shapeToDraw.b[0])}
          y2={round(shapeToDraw.b[1])}
        />
      );
    case 'ellipse':
      return (
        <ellipse
          key={key}
          className={shapeToDraw.cls}
          cx={round(shapeToDraw.c[0])}
          cy={round(shapeToDraw.c[1])}
          rx={round(shapeToDraw.rx)}
          ry={round(shapeToDraw.ry)}
        />
      );
    case 'dot':
      return (
        <circle
          key={key}
          className={shapeToDraw.cls}
          cx={round(shapeToDraw.c[0])}
          cy={round(shapeToDraw.c[1])}
          r={shapeToDraw.r}
        />
      );
    case 'glyph':
      return (
        <text
          key={key}
          className={shapeToDraw.cls}
          x={round(shapeToDraw.c[0])}
          y={round(shapeToDraw.c[1])}
        >
          {shapeToDraw.text}
        </text>
      );
    case 'label':
      return (
        <text
          key={key}
          className={shapeToDraw.cls}
          x={round(shapeToDraw.c[0])}
          y={round(shapeToDraw.c[1])}
        >
          {t(shapeToDraw.message)}
        </text>
      );
    case 'opening':
      return (
        <use
          key={key}
          href={`#${scope}-${shapeToDraw.symbol}`}
          x={round(shapeToDraw.c[0])}
          y={round(shapeToDraw.c[1])}
        />
      );
  }
}

export function AxonometryDrawing({ fit = 'cover' }: AxonometryDrawingProps) {
  const t = useMessages('drawing');
  /* Per-instance ids for the opening blocks. A page drawing this twice — and the landing and
     the entrance are one route apart — would otherwise have two elements sharing an `id`,
     which is invalid markup whose `href="#…"` references all resolve to whichever copy the
     parser saw first. The colons React puts in `useId` are stripped: a fragment identifier
     carrying them is a URL reference nobody should have to reason about. */
  const scope = `kalqa-axonometry-${useId().replace(/:/g, '')}`;

  return (
    <svg
      className={styles.view}
      viewBox="0 0 400 300"
      preserveAspectRatio={fit === 'contain' ? 'xMidYMid meet' : 'xMidYMid slice'}
      aria-hidden
    >
      <defs>
        {OPENINGS.map((entry) => (
          <g key={entry.id} id={`${scope}-${entry.id}`}>
            {entry.shapes.map((child, index) => draw(child, index, scope, t))}
          </g>
        ))}
      </defs>
      <rect className={styles.sheet} width="400" height="300" />
      {SECTIONS.map((shapes, group) => (
        <g key={group}>{shapes.map((child, index) => draw(child, index, scope, t))}</g>
      ))}
    </svg>
  );
}
